import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormInput,
  FormRecordPicker,
  FormTextarea,
} from "components/BaseForm";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import {
  useEmailExemption,
  useEmailExemptionsMutations,
} from "hooks/useEmailExemptionsTable";
import usePageTitle from "hooks/usePageTitle";
import CampaignService from "services/Campaign";
import EmailAddressService from "services/EmailAddress";

const LIST_PATH = "/settings/email_exemptions";

const schema = z.object({
  email_address_id: z.number({
    required_error: "Email address is required",
    invalid_type_error: "Email address is required",
  }),
  campaign_id: z.number({
    required_error: "Campaign is required",
    invalid_type_error: "Campaign is required",
  }),
  justification: z.string().trim().min(1, "Justification is required"),
  source: z.string().trim().min(1, "Source is required"),
  notes: z.string(),
});

const toFilters = search => (search ? { search_query: search } : null);

// Email addresses come back as `{ id, email }`; the picker labels by `name`.
const fetchEmailAddresses = async search => {
  const emails = await EmailAddressService.getEmailAddressesForFilters(
    toFilters(search)
  );
  return emails.map(({ id, email }) => ({ id, name: email }));
};

const fetchCampaigns = search =>
  CampaignService.getCampaignsForFilters(toFilters(search));

const EmailExemption = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailExemption.edit
      : ACL_RELATIONSHIPS.emailExemption.create,
  ]);

  const {
    data: exemption,
    isSuccess: exemptionLoaded,
    isError: exemptionError,
    isFetchedAfterMount,
  } = useEmailExemption(sourceId);
  const { create, update } = useEmailExemptionsMutations();

  const defaultValues = useMemo(
    () => ({
      email_address_id: exemption?.email_address.id ?? null,
      campaign_id: exemption?.campaign.id ?? null,
      justification: exemption?.justification ?? "",
      source: exemption?.source ?? "",
      notes: exemption?.notes ?? "",
    }),
    [exemption]
  );

  const pageTitle = isEdit
    ? "Edit Email Exemption"
    : isClone
      ? "Clone Email Exemption"
      : "New Email Exemption";

  usePageTitle(pageTitle);

  const handleSubmit = async data => {
    if (isEdit) {
      await update.mutateAsync({ id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  const handleSuccess = () => {
    toast.success(
      isEdit ? "Email exemption updated" : "Email exemption created"
    );
    navigate(LIST_PATH);
  };

  const handleCancel = () => navigate(LIST_PATH);

  if (!canWrite) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (exemptionError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this email exemption. Please try again."}</p>
        <Link to={LIST_PATH}>Back to Email Exemptions</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // the form's first render. Mounting on that payload would strand the form on
  // pre-save values, and saving would write them back over a newer edit.
  if (sourceId && !(exemptionLoaded && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle={pageTitle}
          backLink={{ to: LIST_PATH, label: "Back to Email Exemptions" }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <FormRecordPicker
                  name="email_address_id"
                  label="Email Address"
                  required
                  optionsKey="email-exemption-email-addresses"
                  fetchOptions={fetchEmailAddresses}
                  placeholder="Select an email address"
                  display={
                    exemption && {
                      id: exemption.email_address.id,
                      label: exemption.email_address.email,
                    }
                  }
                />
                <FormRecordPicker
                  name="campaign_id"
                  label="Campaign"
                  required
                  optionsKey="email-exemption-campaigns"
                  fetchOptions={fetchCampaigns}
                  placeholder="Select a campaign"
                  display={
                    exemption && {
                      id: exemption.campaign.id,
                      label: exemption.campaign.name,
                    }
                  }
                />
                <FormTextarea
                  name="justification"
                  label="Justification"
                  required
                  rows={6}
                  placeholder="Enter the justification for the email exemption"
                />
                <FormInput
                  name="source"
                  label="Source"
                  required
                  placeholder="Enter the source of the email exemption"
                />
                <FormTextarea
                  name="notes"
                  label="Notes"
                  rows={6}
                  placeholder="Enter any additional notes for the email exemption"
                />
              </div>
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default EmailExemption;
